// B19 / D23 / D24 — sections that are switched off in code.
//
// One entry per hidden or cut section. While `enabled` is false the section is:
//   • out of the app navigation, sidebar search, favorites and Home cards
//     (client/src/components/app-layout.tsx, client/src/lib/home-card-visibility.ts),
//   • unreachable by URL — every in-app route below redirects to /app/dashboard
//     (client/src/components/beta-feature-route.tsx, which wraps the router),
//   • 404 for everyone on the API paths below (server/lib/feature-gates.ts,
//     enforceFeatureGates — runs before auth, admins included),
//   • not running its background jobs (server/schedule-cron.ts checks
//     `isHiddenSectionEnabled` for collections and the trash service).
//
// Most hidden sections keep their implementation. Retired entries (Cameras)
// retain only the saved-link redirect; they cannot be restored with a toggle.
// To bring a hidden section back, flip its `enabled` to true here — that restores
// navigation, URLs, API and jobs together. This is code (not a DB flag) so
// production is correct without any admin toggle.
//
// Path patterns are boundary-aware prefixes ("/app/buy" matches "/app/buy" and
// "/app/buy/12" but not "/app/buy-box"); a ":param" segment matches any one
// path segment. `apiRoutes` are EXACT matches (method + path) for sections
// whose API root is shared with something that stays on.

export type HiddenSectionKey =
  | "collections"
  | "trash_service"
  | "acquisitions"
  | "buy"
  | "deal_opportunities"
  | "renovations"
  | "furnishing_tracker"
  | "property_launch"
  | "certified_pm_listing"
  | "sale_listings"
  | "vendor_marketplace"
  | "private_vendor_fee"
  | "partner_conversations"
  | "cameras";

export interface HiddenSection {
  name: string;
  /** false = switched off. Flip to true to restore the whole section. */
  enabled: boolean;
  /** Who decided and where (build doc / backend plan). */
  decision: string;
  /** In-app route prefixes that redirect to /app/dashboard. */
  routePrefixes: readonly string[];
  /** API prefixes that 404 for every method. */
  apiPrefixes: readonly string[];
  /** Exact API routes that 404 (used when the API root is shared). */
  apiRoutes?: ReadonlyArray<{ method: string; path: string }>;
}

export const HIDDEN_SECTIONS: Record<HiddenSectionKey, HiddenSection> = {
  collections: {
    name: "Collections",
    enabled: false,
    decision: "Cut — build doc B19",
    routePrefixes: ["/app/financials/collections"],
    apiPrefixes: ["/api/app/collections"],
  },
  trash_service: {
    name: "Trash service (CrewHome)",
    enabled: false,
    decision: "Cut — build doc B19",
    routePrefixes: ["/app/operations/trash"],
    apiPrefixes: ["/api/app/trash"],
  },
  acquisitions: {
    name: "Acquisitions",
    enabled: false,
    decision: "Hide — build doc B19",
    routePrefixes: ["/app/acquisitions"],
    apiPrefixes: ["/api/app/acquisitions"],
  },
  buy: {
    name: "Buy",
    enabled: false,
    decision: "Hide — build doc B19",
    // /app/realtor-listings (realtor directory, D20) is intentionally NOT here.
    routePrefixes: ["/app/buy"],
    apiPrefixes: ["/api/app/buy-deals", "/api/app/wholesaler-listings"],
  },
  deal_opportunities: {
    name: "Deal opportunities",
    enabled: false,
    decision: "Hide — backend plan D24",
    // The sale-listing marketplace browsed from Buy (/app/buy/listing/:id).
    // Sale inquiries stay on: Connections (kept, D24) reads them.
    routePrefixes: ["/app/buy/listing"],
    apiPrefixes: ["/api/app/sell/marketplace"],
  },
  renovations: {
    name: "Renovation tracker",
    enabled: false,
    decision: "Hide — build doc B19",
    routePrefixes: ["/app/renovations"],
    apiPrefixes: [
      "/api/app/renovation-defaults",
      "/api/app/renovation-settings",
      "/api/app/renovation-items",
      "/api/app/properties/:propertyId/renovation-items",
    ],
  },
  furnishing_tracker: {
    name: "Furnishing tracker",
    enabled: false,
    decision: "Hide — build doc B19",
    // The furnishing catalog (Recommended Furnishings) is a keep and stays on.
    routePrefixes: ["/app/furnishing-address-book", "/app/properties/:id/furnishing"],
    apiPrefixes: [
      "/api/app/furnishing-address-book",
      "/api/app/furnishing-orders",
      "/api/app/furnishing-room-labels",
      "/api/app/furnishing-shipping-addresses",
      "/api/app/properties/:propertyId/furnishing-orders",
      "/api/app/properties/:propertyId/furnishing-room-labels",
      "/api/app/properties/:propertyId/furnishing-shipping-addresses",
      "/api/app/properties/:propertyId/apply-furnishing-package",
    ],
  },
  property_launch: {
    name: "Launch tracker",
    enabled: false,
    decision: "Hide — build doc B19",
    // Launch uses the shared properties API, which stays on.
    routePrefixes: ["/app/property-launch"],
    apiPrefixes: [],
  },
  certified_pm_listing: {
    name: "Certified PM listing (in-app add-on)",
    enabled: false,
    decision: "Hide — backend plan D24",
    // In-app it is only the "Certified PM listing" add-on toggle on the legacy
    // plan builder (client/src/components/pricing/module-toggles.tsx).
    routePrefixes: [],
    apiPrefixes: [],
  },
  sale_listings: {
    name: "Sale listings",
    enabled: false,
    decision: "Hide — backend plan D24",
    routePrefixes: ["/app/sell"],
    apiPrefixes: ["/api/app/sell/listings"],
  },
  vendor_marketplace: {
    name: "Vendor marketplace",
    enabled: false,
    decision: "Hide — backend plan D24",
    routePrefixes: ["/app/operations/marketplace"],
    apiPrefixes: [],
    // Only the browse endpoints. `/:vendorId/add` and `/:vendorId/relationship`
    // stay on: the Vendor Directory uses them for phone-match adds and roles.
    apiRoutes: [
      { method: "GET", path: "/api/app/operations/marketplace" },
      { method: "GET", path: "/api/app/operations/marketplace/:vendorId" },
      { method: "GET", path: "/api/app/operations/marketplace/:vendorId/reviews" },
    ],
  },
  private_vendor_fee: {
    name: "$19 private-vendor fee",
    enabled: false,
    decision: "Cut — build doc §2 and backend plan D24",
    // UI only: the "Vendors kept off Marketplace" counter on the legacy plan builder.
    routePrefixes: [],
    apiPrefixes: [],
  },
  partner_conversations: {
    name: "Partner conversations",
    enabled: false,
    decision: "Hide — backend plan D24",
    // Partner threads stay on: Connections (kept, D24) shows them.
    routePrefixes: ["/app/partner-conversations"],
    apiPrefixes: [],
  },
  cameras: {
    name: "Cameras",
    enabled: false,
    decision: "Retired — redirect saved links to the dashboard without mounting a page",
    routePrefixes: ["/app/devices/cameras"],
    apiPrefixes: [],
  },
};

export const HIDDEN_SECTION_KEYS = Object.keys(HIDDEN_SECTIONS) as HiddenSectionKey[];

export function isHiddenSectionEnabled(key: HiddenSectionKey): boolean {
  return HIDDEN_SECTIONS[key].enabled;
}

function splitPath(path: string): string[] {
  const pathname = path.split(/[?#]/, 1)[0];
  return pathname.split("/").filter((s) => s.length > 0);
}

/** Boundary-aware prefix match where a `:param` pattern segment matches any one segment. */
export function pathMatchesPattern(path: string, pattern: string, exact = false): boolean {
  const p = splitPath(path);
  const q = splitPath(pattern);
  if (p.length < q.length) return false;
  if (exact && p.length !== q.length) return false;
  for (let i = 0; i < q.length; i++) {
    if (q[i].startsWith(":")) continue;
    if (q[i] !== p[i]) return false;
  }
  return true;
}

/** The switched-off section whose in-app route covers `path`, if any. */
export function hiddenSectionForAppPath(path: string): HiddenSectionKey | null {
  for (const key of HIDDEN_SECTION_KEYS) {
    const section = HIDDEN_SECTIONS[key];
    if (section.enabled) continue;
    if (section.routePrefixes.some((prefix) => pathMatchesPattern(path, prefix))) return key;
  }
  return null;
}

export function isHiddenAppPath(path: string): boolean {
  return hiddenSectionForAppPath(path) !== null;
}

/** The switched-off section whose API covers `method path`, if any. */
export function hiddenSectionForApi(method: string, path: string): HiddenSectionKey | null {
  const upper = method.toUpperCase();
  const m = upper === "HEAD" ? "GET" : upper;
  for (const key of HIDDEN_SECTION_KEYS) {
    const section = HIDDEN_SECTIONS[key];
    if (section.enabled) continue;
    if (section.apiPrefixes.some((prefix) => pathMatchesPattern(path, prefix))) return key;
    if (section.apiRoutes?.some((r) => r.method.toUpperCase() === m && pathMatchesPattern(path, r.path, true))) {
      return key;
    }
  }
  return null;
}
